import RSelect, {
  GroupBase,
  Props as ReactSelectProps,
  SelectInstance,
} from "react-select";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import MenuList from "./components/MenuList";
import MultiValue from "./components/MultiValue";
import ValueContainer from "./components/ValueContainer";
import DropdownIndicator from "./components/DropdownIndicator";
import Option from "./components/Option";
import { classNames } from "./styles";
import { forwardRef, ReactElement } from "react";

const ariaLiveMessages = {
  guidance: () => "",
  onChange: () => "",
  onFilter: () => "",
  onFocus: () => "",
};

interface SelectProps {
  className?: string;
  options: SelectItem[];
  isMulti?: boolean;
  size?: "small" | "default" | "big";
  value: SelectItem | SelectItem[] | null;
  onChange: (value: SelectItem | SelectItem[] | null) => void;
  placeholder?: string;
  invalid?: boolean;
  newOption?: SelectNewOption; // Using when we want to allow user tu create new option
  multivalueTagLabelKey?: string; // Translate key to show on tag when using multivalue
  menuListFooter?: React.ReactNode;
  noOptionsText?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  instanceId?: string;
}

export interface SelectItem {
  value: string;
  label: string;
  description?: ReactElement | string;
}

export interface SelectNewOption {
  label: string;
  onClick: () => void;
}

// Extend react-select's Props with your custom newOption
export type CustomSelectProps = ReactSelectProps<
  SelectItem,
  boolean,
  GroupBase<SelectItem>
> & {
  newOption?: SelectNewOption;
  multivalueTagLabelKey?: string;
  menuListFooter?: React.ReactNode;
};

const Select = forwardRef<
  SelectInstance<SelectItem, false, GroupBase<SelectItem>>,
  SelectProps
>(
  (
    {
      options,
      value,
      onChange,
      isMulti,
      placeholder,
      invalid,
      newOption,
      multivalueTagLabelKey,
      menuListFooter,
      noOptionsText,
      className,
      isClearable = false,
      isDisabled,
      instanceId,
    },
    ref,
  ) => {
    const { tInput } = useCustomTranslation();

    return (
      <RSelect
        options={options}
        classNamePrefix="Select"
        className={`Select ${className}`}
        /* @ts-expect-error Bug in the react-select, based on documentation isMulti is boolean, but types are only false and undefined*/
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        components={{
          Option: Option,
          DropdownIndicator: DropdownIndicator,
          ValueContainer: ValueContainer,
          MultiValue: MultiValue,
          MenuList: MenuList,
        }}
        classNames={classNames(invalid)}
        // styles={styles(invalid)}
        hideSelectedOptions={false}
        isClearable={isClearable}
        onChange={onChange}
        value={value}
        ariaLiveMessages={ariaLiveMessages}
        screenReaderStatus={() => ""}
        placeholder={placeholder}
        noOptionsMessage={() => noOptionsText ?? tInput("select_no_options")}
        newOption={newOption}
        multivalueTagLabelKey={multivalueTagLabelKey}
        menuListFooter={menuListFooter}
        ref={ref}
        isDisabled={isDisabled}
        instanceId={instanceId}
      />
    );
  },
);

export default Select;
